import React, { type HTMLAttributes, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  Container,
  Icon,
  useWindowDimensions,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableEntity, LinkField, ReferenceField } from '@uncinc/react-drupal-core/types';
import { BreadcrumbsV2, CoreLink } from '@uncinc/react-drupal-core';

import type { ProfileDefault } from '../../../../types/uncinc_profile/profile';
import type { TaxonomyLocation } from '../../../../types/uncinc_profile/taxonomy';
import {
  ParagraphsRenderer,
  SearchMoreLikeThis,
  Tags,
  VariableRatiosPicture,
} from '../../../../components';
import { taxonomyTagEntityMapper } from '../../../../components/Tags/taxonomyTagMapper';
import useLayoutVariables from '../../../../hooks/useLayoutVariables';

import { SCREEN_MD } from '../../../../config/constants';

import './style.scss';

interface ProfileDetailsProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  telephone?: string;
  email?: string;
  profileLocations?: ReferenceField<TaxonomyLocation>[];
  link?: LinkField;
}

const ProfileDetails: React.FC<ProfileDetailsProps> = ({
  telephone,
  email,
  profileLocations,
  link,
}) => (
  <div className="ProfilePage__hero__details">
    <div className="ProfilePage__hero__links">
      {telephone && (
        <CoreLink
          className="ProfilePage__hero__links__link ProfilePage__hero__links__link__telephone"
          href={`tel:${telephone}`}
        >
          <Icon name="call" flair="outlined" />{telephone}
        </CoreLink>
      )}
      {email && (
        <CoreLink
          className="ProfilePage__hero__links__link ProfilePage__hero__links__link__email"
          href={`mailto:${email}`}
        >
          <Icon name="mail" flair="outlined" />{email}
        </CoreLink>
      )}
      {(profileLocations && profileLocations.length > 0) && (
        <div className="ProfilePage__hero__locations">
          <Icon name="location_on" flair="outlined" />
          <Tags items={taxonomyTagEntityMapper(profileLocations)} />
        </div>
      )}
      {link && (
        <CoreLink
          className="ProfilePage__hero__links__link ProfilePage__hero__links__link__external"
          href={link.alias || link.uri}
        >
          <Icon name="arrow_outward" flair="outlined" />{link.title || link.alias || link.uri}
        </CoreLink>
      )}
    </div>
  </div>
);

interface ProfileProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<ProfileDefault>;
}

const ProfilePage: React.FC<ProfileProps> = ({
  entity,
  className,
}) => {
  const classes = classNames(className, 'Node', 'ProfilePage');
  const { t } = useTranslation();
  const { width } = useWindowDimensions();

  const isScreenMedium = useMemo(() => (
    width >= SCREEN_MD
  ), [width]);

  const {
    heroImageBreakpointAspectRatios,
    heroImageColClasses,
    breadcrumbsColClasses,
    heroDetailsColClasses,
    bodyBelowHero,
    heroBodyColClasses, // only used if bodyBelowHero is true
  } = useLayoutVariables('profilePage');

  const profile = entity.toJS();
  const profileId = profile.profile_id[0].value;
  const lang = profile.langcode[0].value;
  const profileFirstName = profile.field_first_name[0].value;
  const profileLastName = profile.field_last_name[0]?.value;
  const fullName = [profileFirstName, profileLastName].join(' ');
  const profileDescription = profile.field_description?.[0]?.processed;
  const email = profile.field_email_address?.[0]?.value;
  const profileLabels = profile.field_labels;
  const profileFunctions = profile.field_functions;
  const profileLocations = profile.field_locations;
  const profilePicture = profile.field_media_item[0];
  const telephone = profile.field_telephone?.[0]?.value;
  const link = profile.field_link[0];

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="ProfilePage__hero">
        <Container>
          <div className="row">
            <div className={breadcrumbsColClasses}>
              <BreadcrumbsV2 currentPageTitle={fullName} />
            </div>
          </div>
          <div className="row">
            <div className={heroImageColClasses}>
              <div className="ProfilePage__hero__left">
                {profilePicture && (
                  <div className="ProfilePage__hero__image">
                    <VariableRatiosPicture
                      image={profilePicture}
                      aspectRatios={heroImageBreakpointAspectRatios}
                    />
                  </div>
                )}
                {(isScreenMedium && !bodyBelowHero) && (
                  <ProfileDetails
                    telephone={telephone}
                    email={email}
                    profileLocations={profileLocations}
                    link={link}
                  />
                )}
              </div>
            </div>
            <div className={heroDetailsColClasses}>
              <div className="ProfilePage__hero__right">
                <h2 itemProp="name" className="ProfilePage__hero__name">{fullName}</h2>

                <div className="ProfilePage__hero__tags">
                  <div className="ProfilePage__hero__functions">
                    {(profileFunctions && profileFunctions.length > 0) && (
                      <Tags items={taxonomyTagEntityMapper(profileFunctions)} />
                    )}
                  </div>
                  <div className="ProfilePage__hero__labels">
                    {(profileLabels && profileLabels.length > 0) && (
                      <Tags items={taxonomyTagEntityMapper(profileLabels)} />
                    )}
                  </div>
                </div>

                {(profileDescription && !bodyBelowHero) && (
                  <WysiwygField
                    className="ProfilePage__hero__body body--large"
                    property="schema:text"
                    html={profileDescription}
                  />
                )}

                {(!isScreenMedium || bodyBelowHero) && (
                  <ProfileDetails
                    telephone={telephone}
                    email={email}
                    profileLocations={profileLocations}
                    link={link}
                  />
                )}

              </div>
            </div>
          </div>
          {bodyBelowHero && (
            <div className="row">
              <div className={heroBodyColClasses}>
                <WysiwygField
                  className="ProfilePage__hero__body ProfilePage__hero__body-below-hero body--large"
                  property="schema:text"
                  html={profileDescription}
                />
              </div>
            </div>
          )}
        </Container>
      </div>

      <ParagraphsRenderer className="NodeLandingPage__paragraphs" paragraphs={entity.get('field_paragraphs')} />

      <div className="NodeProfilePage__related-content">
        <Container>
          <div className="row">
            <div className="col-12 col-xl-10 offset-xl-1">
              <SearchMoreLikeThis
                entityId={profileId}
                langcode={lang}
                contentTypes={['default_profile']}
                fields={['profile_locations_name', 'profile_functions_name']}
                title={t('content_types.profile.related_content')}
              />
            </div>
          </div>
        </Container>
      </div>
    </article>
  );
};

export default ProfilePage;
